import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import AddIcon from "@mui/icons-material/Add";
import DashboardWidget from "../../widgets/ContainerWidget/DashboardWidget";
import DashboardWidgetBody from "../../widgets/ContainerWidget/DashboardWidgetBody";
import { defaultIconStyling } from "./helper";
import Workspace from "./Workspace";
import debounce from "lodash.debounce";
import { useIntl } from "react-intl";
import {  
  useWorkspaceAdminContext,
} from "../../../hooks/useWorkspaceAdminContext";
import WorkspaceAdminProvider from "../../../context/WorkspaceAdminContext";

const MAX_WORKSPACES = 13; // limit per "page"

const useStyles = makeStyles(() => ({
  body: {
    background: "transparent",
    overflowY: "auto",
    padding: 16,
    paddingTop: 4,
  },
  title: {
    color: (props) => props.titleColor || "#212529",
    fontSize: 16,
  },
  workspaceContainer: {
    background: "transparent",
  },
  searchBox: {
    marginBottom: 8,
  },
  moreButton: {
    fontSize: 12,
    marginTop: 6,
    textAlign: "center",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
  },
}));

const dividerColorFn = (props) => `1px solid ${props.dividerColor || "#dee2e6"}`;

const useItemStyles = makeStyles((theme) => ({
  root: {
    background: (props) => props.contentBG || "#ffffff",
    borderRadius: 4,
    borderTop: dividerColorFn, // dividerColor
    cursor: "pointer",
    flexWrap: "nowrap",
    padding: 6,
    "&.specialItem": {
      borderTop: "none",
    },
    "&:hover": {
      background:
        (props) =>
          props.contentBGhoverColor || theme.palette.secondary.light,
    },
    "&:last-of-type": {
      borderBottom: dividerColorFn, // dividerColor
    },
  },
  name: {
    color: "#555",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  description: {
    color: "#9a9a9a",
    fontSize: 12,
  },
  iconContainer: {
    marginRight: 7,
    minWidth: 44,
    textAlign: "center",
  },
}));

const getUpdatedIconStyling = (iconStyling) => {
  if (!iconStyling) {
    return defaultIconStyling;
  }

  const getValue = (name) => iconStyling[name] || defaultIconStyling[name];
  const setValue = (o, name) => (o[name] = getValue(name));

  const styling = {};
  setValue(styling, "borderColor");
  setValue(styling, "borderRadius");
  setValue(styling, "borderWidth");
  setValue(styling, "boxShadow");

  return styling;
};

/**
 * Outer wrapper that provides WorkspaceAdmin context
 */
const WorkspaceWidgetComponent = (props) => {
  return (
    <WorkspaceAdminProvider>
      <WorkspaceWidgetInner {...props} />
    </WorkspaceAdminProvider>
  );
};

/**
 * Inner component that consumes WorkspaceAdmin context
 */
const WorkspaceWidgetInner = ({ layout, onChangeWorkspace, workspaces }) => {
  const {
    contentBG,
    contentBGhoverColor,
    dividerColor,
    name: title,
    titleColor,
    iconStyling,
  } = layout || {};

  const classes = useStyles({ titleColor });
  const workspaceAdminContext = useWorkspaceAdminContext();
  const itemClasses = useItemStyles({
    contentBG,
    contentBGhoverColor,
    dividerColor,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const intl = useIntl();
  const [visibleCount, setVisibleCount] = useState(MAX_WORKSPACES);

  // debounced search handler
  const debouncedSetSearch = useCallback(
    debounce((val) => {
      setSearchTerm(val.toLowerCase());
      setVisibleCount(MAX_WORKSPACES); // reset when searching
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSetSearch(e.target.value);
  };

  const updatedStyling = getUpdatedIconStyling(iconStyling);

  const filteredWorkspaces = useMemo(() => {
    if (!Array.isArray(workspaces)) return [];
    if (!searchTerm) return workspaces;
    return workspaces.filter(
      (w) =>
        w?.name?.toLowerCase().includes(searchTerm) ||
        w?.description?.toLowerCase().includes(searchTerm)
    );
  }, [workspaces, searchTerm]);

  const shownWorkspaces = filteredWorkspaces.slice(0, visibleCount);
  const extraCount = filteredWorkspaces.length - shownWorkspaces.length;

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + MAX_WORKSPACES);
  };

  return (
    <DashboardWidget>
      <DashboardWidgetBody customClass={classes.body}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {title && (
            <Typography component="span" className={classes.title}>
              {title}
            </Typography>
          )}

          {workspaceAdminContext.canManageWorkspaces && (
            <IconButton onClick={() => workspaceAdminContext.openCreateModal()}>
              <AddIcon />
            </IconButton>
          )}
        </Box>

        {/* Search box */}
        <TextField
          placeholder={intl.formatMessage({ id: "nav.search" })}
          variant="outlined"
          size="small"
          fullWidth
          onChange={handleSearchChange}
          className={classes.searchBox}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <i className="nc-icon nc-zoom-split" />
              </InputAdornment>
            ),
          }}
        />

        <Box className={classes.workspaceContainer}>
          {shownWorkspaces.map((workspace, idx) => (
            <Workspace
              classes={itemClasses}
              className={!title && idx === 0 ? "specialItem" : ""}
              iconStyling={updatedStyling}
              key={idx}
              onClick={onChangeWorkspace}
              workspace={workspace}
            />
          ))}
          {extraCount > 0 && (
            <Button
              onClick={handleShowMore}
              className={classes.moreButton}
              variant="text"
              size="small"
            >
              {intl.formatMessage({ id: 'widget.extraWorkspaces' }, { extraCount })}
            </Button>
          )}
        </Box>
      </DashboardWidgetBody>
    </DashboardWidget>
  );
};

export default WorkspaceWidgetComponent;
